import { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@boundary/components/input";
import { Button } from "@boundary/components/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@boundary/components/dialog";
import { usePromo } from "@boundary/_hooks";
import { X } from "lucide-react";
import { toast } from "sonner";
import styles from "./promoCodeInput.module.scss";

interface PromoCodeInputProps {
  onCartRecalculate: () => Promise<void> | void;
  /** Вызывается когда меняется состояние применения (загрузка) */
  onApplyingChange?: (isApplying: boolean) => void;
  /** Вызывается когда меняется фокус на поле ввода */
  onFocusChange?: (isFocused: boolean) => void;
}

export function PromoCodeInput({ onCartRecalculate, onApplyingChange, onFocusChange }: PromoCodeInputProps) {
  const { activePromo, activatePromo, deactivatePromo, loadActivePromo } = usePromo();
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isApplying, setIsApplyingInternal] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  // Обёртка для setIsApplying, которая также вызывает колбэк
  const setIsApplying = useCallback((value: boolean) => {
    setIsApplyingInternal(value);
    onApplyingChange?.(value);
  }, [onApplyingChange]);
  
  // Проверяем есть ли непримененный промокод
  const hasUnappliedPromo = inputValue.trim() !== "" && inputValue.trim() !== (activePromo?.code ?? "");
  
  // Уведомляем родителя об изменении фокуса
  useEffect(() => {
    onFocusChange?.(isFocused);
  }, [isFocused, onFocusChange]);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  // Загружаем активный промокод при монтировании и устанавливаем начальное значение
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      await loadActivePromo();
      // После загрузки - пересчитываем корзину
      if (mounted) {
        onCartRecalculate();
      }
    };
    
    void init();
    
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Только при монтировании!

  // Устанавливаем значение инпута из activePromo ТОЛЬКО когда activePromo меняется извне
  // (например, после успешной активации или деактивации)
  // НЕ синхронизируем, если пользователь редактирует вручную
  const prevActivePromoRef = useRef<string | null>(null);
  
  useEffect(() => {
    const currentCode = activePromo?.code ?? null;
    const prevCode = prevActivePromoRef.current;
    
    // Синхронизируем только если activePromo изменился (не при каждом рендере)
    if (currentCode !== prevCode) {
      prevActivePromoRef.current = currentCode;
      setInputValue(currentCode ?? "");
      setError(null);
    }
  }, [activePromo]);

  // Применение промокода (по Enter или blur)
  const applyPromo = useCallback(async (code: string) => {
    const trimmed = code.trim();
    
    // Если пустое и есть активный — ничего не делаем (очистка через X)
    if (!trimmed) return;
    
    // Если уже применён тот же код — ничего не делаем
    if (activePromo?.code === trimmed) return;

    // Если уже есть другой активный промокод — диалог замены
    if (activePromo && activePromo.code !== trimmed) {
      setPendingCode(trimmed);
      setShowReplaceDialog(true);
      return;
    }

    // Применяем промокод
    setIsApplying(true);
    setError(null);
    try {
      await activatePromo(trimmed);
      await onCartRecalculate(); // Ждём пересчёт корзины
      toast.success("Промокод применён!");
    } catch (err: any) {
      const errorMessage = err?.description || err?.message || "Не удалось применить промокод";
      
      if (errorMessage.includes("не найден") || err?.code === 404) {
        setError("Промокод не найден");
      } else if (errorMessage.includes("истек") || errorMessage.includes("expired")) {
        setError("Промокод истёк");
      } else if (errorMessage.includes("использован") || errorMessage.includes("исчерпан") || errorMessage.includes("used")) {
        setError("Промокод уже использован");
      } else {
        setError(errorMessage);
      }
      
      // При ошибке восстанавливаем старое значение input
      setInputValue(activePromo?.code || "");
      
      // Пересчитываем корзину чтобы UI был консистентен
      try {
        await onCartRecalculate();
      } catch {
        // Игнорируем ошибку пересчёта
      }
    } finally {
      setIsApplying(false);
    }
  }, [activePromo, activatePromo, deactivatePromo, onCartRecalculate]);

  // Просто меняем значение, без применения
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setError(null);
  }, []);

  // Применяем по Enter
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void applyPromo(inputValue);
    }
  }, [applyPromo, inputValue]);

  // Обработчик фокуса
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  // Применяем по потере фокуса или восстанавливаем старое значение
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    
    const trimmed = inputValue.trim();
    
    // Если поле пустое, но есть активный промокод - восстанавливаем значение
    // (чтобы очистить промокод, нужно использовать кнопку X)
    if (!trimmed && activePromo) {
      setInputValue(activePromo.code);
      return;
    }
    
    void applyPromo(inputValue);
  }, [applyPromo, inputValue, activePromo]);

  const handleReplaceConfirm = useCallback(async () => {
    if (!pendingCode) return;
    
    setIsApplying(true);
    setShowReplaceDialog(false);
    setError(null);
    
    try {
      await activatePromo(pendingCode);
      setInputValue(pendingCode);
      await onCartRecalculate(); // Ждём пересчёт
      toast.success("Промокод заменён!");
    } catch (err: any) {
      const errorMessage = err?.description || err?.message || "Не удалось применить промокод";
      setError(errorMessage);
      // Возвращаем старое значение
      setInputValue(activePromo?.code || "");
      
      // Пересчитываем корзину чтобы UI был консистентен
      try {
        await onCartRecalculate();
      } catch {
        // Игнорируем ошибку пересчёта
      }
    } finally {
      setIsApplying(false);
      setPendingCode(null);
    }
  }, [pendingCode, activatePromo, activePromo, onCartRecalculate]);

  const handleReplaceCancel = useCallback(() => {
    setShowReplaceDialog(false);
    setPendingCode(null);
    // Возвращаем старое значение
    setInputValue(activePromo?.code || "");
  }, [activePromo]);

  const handleClear = useCallback(async () => {
    setInputValue("");
    setError(null);
    if (activePromo) {
      setIsApplying(true);
      try {
        await deactivatePromo();
        await onCartRecalculate(); // Ждём пересчёт
        toast.success("Промокод удалён");
      } catch (err) {
        console.error("[PromoCodeInput] Failed to deactivate promo:", err);
      } finally {
        setIsApplying(false);
      }
    }
  }, [activePromo, deactivatePromo, onCartRecalculate]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.inputRow}>
          <div className={styles.inputContainer}>
            <Input
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder="Введите промокод"
              disabled={isApplying}
              className={error ? styles.inputError : undefined}
            />
            {/* Показываем крестик только когда есть применённый промокод и поле не в фокусе */}
            {inputValue && activePromo && !isFocused && !hasUnappliedPromo && (
              <button
                type="button"
                onClick={handleClear}
                className={styles.clearButton}
                disabled={isApplying}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        {error && (
          <p className={styles.errorMessage}>{error}</p>
        )}
        {activePromo && !error && !isFocused && (
          <p className={styles.successMessage}>
            Применен: {activePromo.description}
          </p>
        )}
      </div>

      <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Заменить промокод?</DialogTitle>
            <DialogDescription>
              У вас уже применен промокод <strong>{activePromo?.code}</strong>.
              Заменить на <strong>{pendingCode}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={handleReplaceCancel} disabled={isApplying}>
              Нет
            </Button>
            <Button onClick={handleReplaceConfirm} disabled={isApplying}>
              {isApplying ? "Применение..." : "Да"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

